import { useEffect, useCallback, useRef } from "react";
import { useAppSelector } from "@/redux/store";
import { FCMService } from "@/firebase/fcmService";
import { toast } from "sonner";
import { isPushCapableBrowser } from "@/lib/browserDetection";

/**
 * Custom hook for managing FCM functionality
 */
export const useFCM = () => {
  const { isLoggedIn, accessToken } = useAppSelector((state) => state.login);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const messageListenerRef = useRef<((event: MessageEvent) => void) | null>(null);

  // Handle foreground messages
  const handleForegroundMessage = useCallback((payload: any) => {
    console.log("Foreground message received:", payload);
    
    // Show notification using toast
    if (payload.notification) {
      toast(payload.notification.title || "New Message", {
        description: payload.notification.body,
        duration: 5000,
      });
    }
  }, []);

  // Initialize FCM when user logs in
  const initializeFCM = useCallback(async () => {
    if (typeof window === 'undefined' || !isLoggedIn || !accessToken) {
      return;
    }

    if (!isPushCapableBrowser()) {
      console.warn("FCM: Skipping initialization - browser cannot support push");
      return;
    }

    try {
      console.log("Initializing FCM...");
      const success = await FCMService.initializeFCM(handleForegroundMessage);
      
      if (success) {
        console.log("FCM initialized successfully");
      } else {
        console.warn("FCM initialization failed");
      }
    } catch (error) {
      console.error("Error initializing FCM:", error);
    }
  }, [isLoggedIn, accessToken, handleForegroundMessage]);

  // Cleanup FCM when user logs out
  const cleanupFCM = useCallback(async () => {
    // Only attempt cleanup on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      console.log("Cleaning up FCM...");
      await FCMService.deleteTokenAndCleanup();
      
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      // Remove message listener
      if (messageListenerRef.current && "serviceWorker" in navigator && navigator.serviceWorker) {
        try {
          navigator.serviceWorker.removeEventListener("message", messageListenerRef.current);
        } catch (error) {
          console.warn("Error removing service worker listener:", error);
        }
        messageListenerRef.current = null;
      }

      console.log("FCM cleanup completed");
    } catch (error) {
      console.error("Error cleaning up FCM:", error);
      // Don't let FCM cleanup errors break the app
    }
  }, []);

  // Set up token refresh listener
  const setupTokenRefresh = useCallback(() => {
    if (typeof window === 'undefined' || !("serviceWorker" in navigator)) {
      return;
    }

    try {
      // Remove existing listener if any
      if (messageListenerRef.current) {
        navigator.serviceWorker.removeEventListener("message", messageListenerRef.current);
      }

      // Create new listener
      const messageListener = (event: MessageEvent) => {
        if (event.data && event.data.type === "FCM_TOKEN_REFRESH") {
          FCMService.handleTokenRefresh(event.data.token);
        }
      };

      messageListenerRef.current = messageListener;

      // Wait for service worker to be ready before adding listener
      navigator.serviceWorker.ready.then(() => {
        navigator.serviceWorker.addEventListener("message", messageListener);
      }).catch((error) => {
        console.warn("Error waiting for service worker:", error);
      });
    } catch (error) {
      console.warn("Error setting up token refresh listener:", error);
    }
  }, []);

  // Initialize FCM when user logs in
  useEffect(() => {
    if (isLoggedIn && accessToken) {
      // Longer delay to ensure everything is properly loaded
      const timer = setTimeout(() => {
        // Only initialize if we're in a browser environment
        if (typeof window !== 'undefined' && window.isSecureContext) {
          initializeFCM();
          setupTokenRefresh();
        }
      }, 2000);

      return () => clearTimeout(timer);
    } else if (!isLoggedIn) {
      // Cleanup when user logs out
      cleanupFCM();
    }
  }, [isLoggedIn, accessToken, initializeFCM, setupTokenRefresh, cleanupFCM]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (messageListenerRef.current && "serviceWorker" in navigator && navigator.serviceWorker) {
        try {
          navigator.serviceWorker.removeEventListener("message", messageListenerRef.current);
        } catch (error) {
          // Ignore errors during cleanup
        }
      }
    };
  }, []);

  return {
    initializeFCM,
    cleanupFCM,
  };
};
